/**
 * Briques d'interface partagées.
 *
 * Chaque écran gère explicitement ses trois états : chargement, vide, erreur.
 * Un état vide porte toujours une explication — jamais un dessin seul.
 */
import type { ReactNode } from 'react';
import { ErreurApi, type StatutMessage } from '../api/client';
import { aspectStatut } from '../lib/format';

export function Chargement({ quoi = 'Chargement…' }: { quoi?: string }) {
  return (
    <div className="chargement" role="status" aria-live="polite">
      {quoi}
    </div>
  );
}

export function EtatVide({ titre, children }: { titre: string; children?: ReactNode }) {
  return (
    <div className="etat-vide">
      <p>{titre}</p>
      {children}
    </div>
  );
}

/**
 * Le message français de l'API est affiché tel quel : il a été écrit pour être
 * lu. Le code technique reste visible et sélectionnable — c'est ce que le
 * support demandera.
 */
export function MessageErreur({ erreur }: { erreur: unknown }) {
  if (!erreur) return null;
  const texte = erreur instanceof Error ? erreur.message : 'Une erreur inattendue est survenue.';
  const code = erreur instanceof ErreurApi ? erreur.code : null;
  return (
    <div className="message-erreur" role="alert">
      {texte}
      {code && <code>{code}</code>}
    </div>
  );
}

export function MessageSucces({ children }: { children: ReactNode }) {
  return (
    <div className="message-succes" role="status">
      {children}
    </div>
  );
}

export function Bandeau({
  ton = 'orange',
  titre,
  children,
}: {
  ton?: 'orange' | 'rouge' | 'vert';
  titre?: string;
  children: ReactNode;
}) {
  return (
    <div className={`bandeau ${ton}`}>
      {titre && <b>{titre}</b>}
      {children}
    </div>
  );
}

/** Pastille d'état. Le libellé est toujours présent : jamais la couleur seule. */
export function Puce({ valeur }: { valeur: StatutMessage }) {
  const { libelle, ton } = aspectStatut(valeur);
  return <span className={`puce ${ton}`}>{libelle}</span>;
}

export function PuceLibre({
  ton,
  children,
}: {
  ton: 'ok' | 'attente' | 'neutre' | 'succes' | 'echec';
  children: ReactNode;
}) {
  return <span className={`puce ${ton}`}>{children}</span>;
}

export function Carte({
  titre,
  action,
  children,
  sansCorps,
}: {
  titre?: string;
  action?: ReactNode;
  children: ReactNode;
  sansCorps?: boolean;
}) {
  return (
    <div className="carte">
      {titre && (
        <header>
          <div className="titre">{titre}</div>
          {action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
        </header>
      )}
      {sansCorps ? children : <div className="corps">{children}</div>}
    </div>
  );
}

export function Statistique({
  intitule,
  valeur,
  note,
  ton,
}: {
  intitule: string;
  valeur: ReactNode;
  note?: ReactNode;
  ton?: 'credit' | 'debit';
}) {
  return (
    <div className="stat">
      <div className="intitule">{intitule}</div>
      <div
        className="valeur"
        style={ton ? { color: ton === 'credit' ? 'var(--vert)' : 'var(--rouge)' } : undefined}
      >
        {valeur}
      </div>
      {note && <div className="note">{note}</div>}
    </div>
  );
}

export function Etiquette({ children }: { children: ReactNode }) {
  return <span className="etiquette-sid">{children}</span>;
}

/**
 * Grille de champs qui se replie toute seule. Les formulaires d'ouverture de
 * compte et de création d'utilisateur ont la même forme : autant la décrire une
 * fois.
 */
export function Grille({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 12,
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      }}
    >
      {children}
    </div>
  );
}

export function Champ({
  libelle,
  valeur,
  onChange,
  type = 'text',
  aide,
  requis,
}: {
  libelle: string;
  valeur: string;
  onChange: (valeur: string) => void;
  type?: string;
  aide?: ReactNode;
  requis?: boolean;
}) {
  const id = `champ-${libelle.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="champ">
      <label htmlFor={id}>
        {libelle}
        {requis && <span style={{ color: 'var(--rouge)' }}> *</span>}
      </label>
      <input id={id} type={type} value={valeur} onChange={(e) => onChange(e.target.value)} />
      {aide && (
        <div className="aide" style={{ marginTop: 4 }}>
          {aide}
        </div>
      )}
    </div>
  );
}

/**
 * Fil d'étapes d'un parcours en plusieurs temps.
 *
 * Des pastilles juxtaposées disent mal qu'on avance : rien ne relie l'étape
 * faite à la suivante, et le regard ne sait pas s'il reste un pas ou trois. Le
 * fil continu le montre — chaque jalon porte son rang, le trait qui y mène se
 * remplit quand il est franchi, et l'étape en cours se distingue des étapes
 * déjà faites, qui portent une coche.
 *
 * `index` est l'étape courante, à partir de zéro. Les étapes avant elle sont
 * réputées franchies.
 */
export function FilEtapes({ etapes, index }: { etapes: string[]; index: number }) {
  return (
    <ol
      aria-label="Progression"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${etapes.length}, 1fr)`,
        listStyle: 'none',
        margin: '0 0 20px',
        padding: 0,
      }}
    >
      {etapes.map((libelle, i) => {
        const franchie = i < index;
        const courante = i === index;
        const atteinte = franchie || courante;
        return (
          <li
            key={libelle}
            aria-current={courante ? 'step' : undefined}
            style={{ position: 'relative', textAlign: 'center', paddingTop: 4 }}
          >
            {/* Le trait part du jalon précédent et s'arrête à celui-ci. Il est
                posé sous le disque, qui le masque sur sa largeur. */}
            {i > 0 && (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: 19,
                  right: '50%',
                  width: '100%',
                  height: 2,
                  background: atteinte ? 'var(--navy)' : 'var(--bordure)',
                }}
              />
            )}
            <span
              aria-hidden="true"
              style={{
                position: 'relative',
                display: 'grid',
                placeItems: 'center',
                width: 30,
                height: 30,
                margin: '0 auto 7px',
                borderRadius: '50%',
                fontSize: 12,
                fontWeight: 700,
                border: `2px solid ${atteinte ? 'var(--navy)' : 'var(--bordure)'}`,
                background: franchie ? 'var(--navy)' : 'var(--blanc)',
                color: franchie ? '#fff' : atteinte ? 'var(--navy)' : 'var(--muet)',
              }}
            >
              {franchie ? '✓' : i + 1}
            </span>
            <span
              style={{
                fontSize: 12.5,
                fontWeight: courante ? 600 : 400,
                color: atteinte ? 'var(--navy)' : 'var(--muet)',
              }}
            >
              {libelle}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Aperçu d'un SMS tel qu'il arrivera sur le téléphone du destinataire.
 *
 * Un encadré neutre disait mal ce qu'il montrait : on y lisait un texte sans
 * savoir à quoi il ressemblerait vraiment. Le cadre d'un téléphone donne
 * l'échelle — la largeur de ligne, le nombre de retours, la place que prend
 * l'expéditeur — et c'est précisément ce qu'on veut juger avant d'envoyer à
 * des milliers de personnes.
 *
 * Tout est dessiné en CSS : aucune image à charger, et le rendu suit le thème.
 *
 * L'expéditeur s'affiche en en-tête parce que c'est là qu'un téléphone le met
 * quand il est alphanumérique — à la place du nom du contact.
 */
export function ApercuMobile({
  expediteur,
  corps,
  heure = '09:41',
}: {
  expediteur: string;
  corps: string;
  heure?: string;
}) {
  const vide = corps.trim().length === 0;
  return (
    <div className="telephone">
      <div className="ecran">
        <div className="barre-etat">
          <span className="heure">{heure}</span>
          <span className="indicateurs" aria-hidden="true">
            <svg viewBox="0 0 18 12" className="signal">
              <rect x="0" y="8" width="3" height="4" rx="1" />
              <rect x="5" y="5" width="3" height="7" rx="1" />
              <rect x="10" y="2" width="3" height="10" rx="1" />
              <rect x="15" y="0" width="3" height="12" rx="1" opacity="0.35" />
            </svg>
            <svg viewBox="0 0 26 12" className="pile">
              <rect x="0.6" y="0.6" width="21" height="10.8" rx="2.6" fill="none" strokeWidth="1.2" />
              <rect x="2.4" y="2.4" width="14" height="7.2" rx="1.4" />
              <rect x="23" y="4" width="2" height="4" rx="1" />
            </svg>
          </span>
        </div>

        <div className="entete">
          <span className="pastille" aria-hidden="true">
            {(expediteur || 'E').slice(0, 1).toUpperCase()}
          </span>
          <span className="nom">{expediteur || 'EXPEDITEUR'}</span>
        </div>

        <div className="fil">
          <div className={`bulle${vide ? ' attente' : ''}`}>
            {vide ? 'Votre message apparaîtra ici.' : corps}
          </div>
          <div className="horodatage">{vide ? '—' : 'maintenant'}</div>
        </div>
      </div>
    </div>
  );
}
